import React, { FC, useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Divider,
  Dropdown,
  Layout,
  List,
  Menu,
  Modal,
  Space,
  Typography,
} from 'antd';
import {
  BellOutlined,
  ExclamationCircleOutlined,
  LogoutOutlined,
  UserOutlined,
} from '@ant-design/icons';
import './WebUxLayout.less';
import { useConnection, useUserInfo } from '../../../connection/Application';
import { useHistory } from 'react-router-dom';
import { UserAvatar } from '../../../components/atoms/UserAvatar';
import Title from 'antd/lib/typography/Title';
import { NotificationData } from '../../../entity/Notification';
import './webUx.less';
import Paragraph from 'antd/lib/typography/Paragraph';
import { Anchor } from 'antd';
import relativeTime from 'dayjs/plugin/relativeTime';
import dayjs from 'dayjs';
dayjs.extend(relativeTime);
const { Link } = Anchor;
const { Text } = Typography;

const { Header, Footer } = Layout;
// eslint-disable-next-line @typescript-eslint/ban-types
type Props = {};
export const WebUxHeader: FC<Props> = () => {
  const history = useHistory();
  const user = useUserInfo();
  const connection = useConnection();
  const [notification, setNotification] = useState<NotificationData[]>([]);
  const [unread, setUnread] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [lastUpdate, setLastUpdate] = useState<Date>();
  const [markAllLoading, setMarkAllLoading] = useState<boolean>(false);
  const logout = useCallback(() => {
    Modal.confirm({
      title: 'Logout',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure to logout?',
      okText: 'Logout',
      okType: 'primary',
      onOk() {
        connection.logout().finally(() => history.replace('/'));
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  }, [connection, history]);

  const getNotification = useCallback(async () => {
    const token = connection.hasSession();
    if (token) {
      await connection.get('notification', { userId: user?.userId }).then((response) => {
        setNotification(response ?? []);
        const unreadItems = response?.filter((item: NotificationData) => item.read == false) ?? [];
        setUnread(unreadItems?.length);
        setLastUpdate(new Date());
      });
    }
  }, [connection, user?.userId]);

  const updateNotifiction = useCallback(() => {
    const token = connection.hasSession();
    if (token) {
      connection
        .get('notification', { userId: user?.userId, date: lastUpdate?.toString() })
        .then((response) => {
          setLastUpdate(new Date());
          const result = [...response, ...notification];
          const unreadItems = result.filter((item: NotificationData) => item.read == false);
          setNotification(result ?? []);
          setUnread(unreadItems.length);
        });
    }
  }, [connection, lastUpdate, notification, user?.userId]);

  const loadMore = useCallback(() => {
    const token = connection.hasSession();
    if (token && notification.length >= page * 100) {
      connection.get('notification', { userId: user?.userId, page: page + 1 }).then((response) => {
        const result = [...notification, ...response];
        setNotification(result ?? []);
        setPage(page + 1);
        const unreadItems = response.filter((item: NotificationData) => item.read == false);
        setUnread(unread + unreadItems.length);
      });
    }
  }, [connection, notification, page, unread, user?.userId]);

  useEffect(() => {
    getNotification();
  }, [getNotification]);

  useEffect(() => {
    const timer = setInterval(() => {
      updateNotifiction();
    }, 5 * 60 * 1000);

    return () => clearInterval(timer); // This represents the unmount function, in which you need to clear your interval to prevent memory leaks.
  }, [updateNotifiction]);

  const navigate: any = useCallback(
    (info: { key: string }) => {
      history.push(`${info.key}`);
    },
    [history],
  );

  const onRead = useCallback(
    async (notificationId: number, all?: boolean) => {
      const isMarkAll = !!all;
      if (isMarkAll) {
        setMarkAllLoading(true);
      }

      try {
        await connection.patch(`notification/${user?.userId}`, { notificationId, all });
        await getNotification();
      } finally {
        if (isMarkAll) {
          setMarkAllLoading(false);
        }
      }
    },
    [connection, getNotification, user?.userId],
  );

  const notifications = (
    <List
      className="notification-list"
      style={{ background: '#fff', height: '75vh', overflowY: 'scroll' }}
      bordered={true}
      header={
        <>
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: 'row-reverse',
              background: '#fff',
            }}>
            <Button
              type="link"
              loading={markAllLoading}
              disabled={notification.length === 0}
              onClick={() => onRead(notification[0].id as number, true)}>
              Mark all as Read
            </Button>
          </div>
        </>
      }
      footer={
        notification.length >= page * 100 ? (
          <>
            <div style={{ width: '100%', display: 'flex', flexDirection: 'row-reverse' }}>
              <Button type="link" onClick={() => loadMore()}>
                Show old notifications
              </Button>
            </div>
            <Divider style={{ margin: 0 }} />
          </>
        ) : (
          <></>
        )
      }
      itemLayout="horizontal"
      dataSource={notification}
      loading={markAllLoading}
      renderItem={(item: NotificationData) => (
        <List.Item
          onClick={() => {
            if (item.read == false) {
              onRead(item.id as number, false);
            }

            // Only navigate to internal same-origin paths. Reject external, protocol-relative
            // (//host) or scheme (javascript:, https:) URLs to prevent an open redirect via an
            // attacker-influenced notification url. Use the SPA router rather than a full reload.
            if (item.url && item.url.startsWith('/') && !item.url.startsWith('//')) {
              history.push(item.url);
            }
          }}
          className="list-item-notification"
          style={
            item.read
              ? {
                  backgroundColor: '#ffffff',
                  cursor: 'pointer',
                }
              : {
                  backgroundColor: 'rgb(213 240 255)',
                  cursor: 'pointer',
                }
          }>
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <div
              style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
              <div style={{ paddingRight: '15px' }}>
                <UserAvatar src={item.image} />
              </div>
              <Title level={5} ellipsis={{ rows: 1 }}>
                {item.title}
              </Title>
            </div>
            <Paragraph ellipsis={{ rows: 2 }} style={{ fontSize: 'small', marginBottom: 0 }}>
              {item.description}
            </Paragraph>
            <p style={{ fontSize: 'x-small' }}>{dayjs(item.time).fromNow()}</p>
          </div>
        </List.Item>
      )}
    />
  );

  const profileMenu = (
    <Menu>
      <Menu.Item key="/webux/profile" onClick={navigate} icon={<UserOutlined />}>
        <Anchor affix={true} key="/webux/profile" onClick={navigate}>
          <Link href="/webux/profile" title="Profile" />
        </Anchor>
      </Menu.Item>
      <Menu.Item key="logout" onClick={logout} icon={<LogoutOutlined />}>
        Logout
      </Menu.Item>
    </Menu>
  );

  return (
    <Header className="webux-header">
      <Space size={'middle'}>
        <Dropdown
          overlay={notifications}
          trigger={['click']}
          overlayStyle={{ minWidth: 300, maxWidth: 500 }}>
          <Badge count={unread} offset={[-8, 8]}>
            <Button type={'link'} shape={'circle'} size={'large'}>
              <BellOutlined />
            </Button>
          </Badge>
        </Dropdown>
        <Dropdown overlay={profileMenu} trigger={['click']}>
          <div style={{ cursor: 'pointer' }}>
            <UserAvatar src={user?.image} />
            <Text className="profile-name">
              {user?.firstName} {user?.lastName}
            </Text>
          </div>
        </Dropdown>
      </Space>
    </Header>
  );
};

export const WebUxFooter: FC<Props> = () => {
  return (
    <Footer style={{ textAlign: 'center' }}>
      StridePal ©{new Date().getFullYear()} Created by StridePal
    </Footer>
  );
};
